import { Link } from 'react-router-dom';
import siteConfig from '@/config/siteConfig';
import { encodeToken, getToken } from '@/lib/authUtils';
import { siteLink, stripSitePrefix } from '@/lib/siteLink';

// Cross-site aware Link.
// - If a `site` prop is given AND it points at a DIFFERENT domain from the
//   current one, the URL is rewritten to the destination domain's public URL
//   (external navigation with encoded auth-token relay).
// - Same-site navigation stays as an internal SPA Link (no full page reload).
// - `newTab`: force the navigation to open in a new browser tab instead of
//   replacing the current one — used where the reader is mid-flow (e.g. an
//   infinite-scroll article feed) and shouldn't lose their place.
export function SiteLink({ to, site, children, newTab = false, ...props }) {
  // Same site: drop its prefix on its own domain (`/tech/blog` is `/blog` on tech-n-ai-talks.com).
  let href = siteLink(to);
  const isCrossSite = siteConfig.isMultiDomain && site && site !== siteConfig.siteId;
  if (isCrossSite) {
    // Other site: build the link as that domain serves it, without our internal prefix, so it lands directly
    // instead of bouncing through a redirect (and keeps query strings such as ?topic=ai).
    to = stripSitePrefix(site, to);
    if (site === 'auto')    href = siteConfig.autoUrl(to);
    else if (site === 'coupon')  href = siteConfig.couponUrl(to);
    else if (site === 'finance') href = siteConfig.financeUrl(to);
    else if (site === 'admin')   href = siteConfig.adminUrl(to);
    else if (site === 'dgtg')    href = siteConfig.dgtgUrl(to);
    else if (site === 'games')   href = siteConfig.gamesUrl(to);
    else if (site === 'travel')  href = siteConfig.travelUrl(to);
    else if (site === 'tech')    href = siteConfig.techUrl(to);
    else if (site === 'devmob')  href = siteConfig.devmobUrl(to);
    else if (site === 'vikas')   href = siteConfig.vikasUrl(to);
  }

  // External URL — append encoded auth token if logged in
  if (href.startsWith('http')) {
    const handleClick = (e) => {
      e.preventDefault();
      const token = getToken();
      let finalHref = href;
      if (token) {
        const url = new URL(href);
        url.searchParams.set('auth_token', encodeToken(token));
        finalHref = url.toString();
      }
      if (newTab) {
        window.open(finalHref, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = finalHref;
      }
    };
    return (
      <a href={href} onClick={handleClick} target={newTab ? '_blank' : undefined} rel={newTab ? 'noopener noreferrer' : undefined} {...props}>
        {children}
      </a>
    );
  }

  return newTab
    ? <Link to={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</Link>
    : <Link to={href} {...props}>{children}</Link>;
}

export default SiteLink;
